import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr) return;
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: addr });
      setDone(true);
    } catch {
      // Always show success state — endpoint itself returns 200 even for
      // unknown emails to avoid user-enumeration.
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-sm p-8 md:p-10" data-testid="forgot-password-card">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="forgot-back-login">
          <ArrowLeft className="h-4 w-4" /> Takaisin kirjautumiseen
        </Link>

        {done ? (
          <div data-testid="forgot-success">
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Tarkista sähköpostisi
            </h1>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Jos osoitteella <span className="font-semibold text-slate-900">{email}</span> on Veraso-tili,
              lähetimme sinne salasanan palautuslinkin. Linkki on voimassa 60 minuuttia.
            </p>
            <p className="text-xs text-slate-500 mt-6">
              Etkö löydä viestiä? Tarkista roskapostikansio. Jos käytät Google-kirjautumista, salasanaa ei
              voida palauttaa — kirjaudu "Jatka Googlella" -painikkeella.
            </p>
            <Link to="/login" className="btn-primary w-full py-3.5 mt-6" data-testid="forgot-go-back-to-login">
              Palaa kirjautumissivulle <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
              <Mail className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Unohditko salasanasi?
            </h1>
            <p className="text-slate-600 mt-2">
              Syötä sähköpostiosoitteesi — lähetämme sinulle linkin uuden salasanan asettamiseksi.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Sähköposti</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base"
                  placeholder="name@company.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="btn-primary w-full py-3.5 disabled:opacity-50"
                data-testid="forgot-submit-button"
              >
                {busy ? "Lähetetään..." : "Lähetä palautuslinkki"}
                {!busy && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
